// packages/web/src/components/account/auth-magic-link.tsx
/**
 * Magic-link flow — two steps: input (enter email) → sent (confirmation).
 * Calls authApi.magicRequest → switch to 'sent' step showing success +
 * 60s resend countdown (anti-abuse). Real email delivery is Phase 5; in dev
 * the worker logs the magic URL.
 */

import { useEffect, useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { authApi } from '../../api/auth.js';
import type { AuthMode } from './auth-modal.js';

interface Props {
  step: 'input' | 'sent';
  onSwitchMode: (m: AuthMode) => void;
  onClose: () => void;
}

export function AuthMagicLink({ step, onSwitchMode }: Props) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  // 60s resend countdown.
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  async function send(e?: React.FormEvent) {
    e?.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      await authApi.magicRequest(email.trim().toLowerCase());
      setSentTo(email.trim().toLowerCase());
      setCountdown(60);
      onSwitchMode('magicSent');
    } catch {
      setError(t('auth.invalidEmail'));
    } finally {
      setLoading(false);
    }
  }

  if (step === 'sent') {
    return (
      <>
        <div className="auth-head">
          <span className="auth-head-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <rect width="20" height="16" x="2" y="4" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </span>
          <h2>{t('auth.magicLinkLogin')}</h2>
        </div>
        <div className="auth-body">
          <div className="auth-magic-sent">
            <div className="auth-magic-sent-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>
            <h3>{t('auth.magicSent')}</h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
              <span className="auth-magic-sent-email">{sentTo}</span>
            </p>
            <p className="auth-magic-sent-hint">{t('auth.magicSentHint')}</p>
            <button
              type="button"
              className="auth-resend-btn"
              disabled={countdown > 0}
              onClick={() => send()}
            >
              {t('auth.resend')}{countdown > 0 ? ` (${countdown}s)` : ''}
            </button>
            <div className="auth-switch" style={{ marginTop: 'var(--space-2xl)' }}>
              <button type="button" onClick={() => onSwitchMode('login')}>← {t('auth.welcome')}</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // step === 'input'
  return (
    <>
      <div className="auth-head">
        <span className="auth-head-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </span>
        <h2>{t('auth.magicLinkLogin')}</h2>
      </div>
      <form className="auth-body" onSubmit={send}>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: '0 0 var(--space-2xl)' }}>
          {t('auth.magicSentHint')}
        </p>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-magic-email">{t('auth.email')}</label>
          <input
            id="auth-magic-email"
            className={`auth-field-input${error ? ' has-error' : ''}`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          {error && (
            <div className="auth-field-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}
        </div>
        <button type="submit" className={`auth-btn-primary${loading ? ' is-loading' : ''}`} disabled={loading}>
          {t('auth.magicLinkLogin')}
        </button>
        <div className="auth-switch">
          <button type="button" onClick={() => onSwitchMode('login')}>← {t('auth.welcome')}</button>
        </div>
      </form>
    </>
  );
}
